import { useState } from 'react';
import { Button } from '../../../button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../dialog';
import { Input } from '../../../input';
import { validateEmail } from '../../../../../lib/utils/validate-email';
import LoadingSpinner from '../../../loading-spinner';
import clsx from 'clsx';
import { InviteUserButtonProps } from './types';
import { inviteToWorkspace } from '../../../../../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../select';
import { WorkspaceRole } from '../../../workspace-settings/types';

export function InviteUserButton({
  authUser,
  users,
  fetchWorkspaceInvitations,
}: InviteUserButtonProps) {
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<WorkspaceRole>('viewer');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInvite = async () => {
    setError(null);
    setSuccess(null);

    const errorMessage = validateEmail(inviteEmail);
    if (errorMessage) {
      setError(errorMessage);
      return;
    }

    if (inviteEmail?.trim() === authUser?.email) {
      setError('You cannot invite yourself.');
      return;
    }

    if (users?.some((user) => user.email === inviteEmail?.trim())) {
      setError('This user is already a member of the workspace.');
      return;
    }

    setIsLoading(true);

    try {
      await inviteToWorkspace({
        workspaceId: authUser?.currentWorkspace?.workspace_id,
        invitedEmail: inviteEmail?.trim(),
        role: selectedRole || 'viewer',
      });

      fetchWorkspaceInvitations();

      setSuccess(`Invitation sent successfully to ${inviteEmail?.trim()}.`);
      setInviteEmail('');
    } catch (err) {
      console.error('Error inviting a user into a workspace:', err);
      setError('Failed to send invitation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white hover:text-white"
        >
          Invite user
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite a new user</DialogTitle>
          <DialogDescription>
            Enter the email of the user you’d like to invite to this workspace.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 flex flex-col gap-2">
          <Input
            placeholder="user@example.com"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              if (error) setError(null);
              if (success) setSuccess(null);
            }}
            disabled={isLoading}
          />

          {error && <p className="text-sm text-red-500">{error}</p>}
          {success && <p className="text-sm text-green-600">{success}</p>}
        </div>

        <div className="mt-2 flex flex-col gap-1">
          <p className="text-sm">Select user role:</p>
          <Select
            value={selectedRole}
            onValueChange={(role: WorkspaceRole) => setSelectedRole(role)}
          >
            <SelectTrigger className="flex-1 bg-prompt-safecomwhite font-prompt-safe-com-semantic-options text-prompt-safecomabbey">
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="viewer">Viewer</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            onClick={handleInvite}
            disabled={isLoading}
            className={clsx(
              'hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white',
              isLoading && 'px-11',
            )}
          >
            {isLoading ? <LoadingSpinner strokeWidth={7} /> : 'Send invite'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
